import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from '../components/ui/use-toast';
import {
  getPMTaskLibrary,
  getPMPriorityQueue,
  getPMChecklists,
  getPMOverviewMetrics,
  getPMStaffRoster,
  getPMWorkflows,
  updatePMTask,
  createPMTask,
  restorePMTask,
  completePMTask,
  skipPMTask,
  delegatePMTask,
  reassignPMOrphanedTask,
  deferPMOrphanedTask,
  reorderPMQueue,
  updatePMChecklistStep,
  createPMWorkflow,
  updatePMWorkflow,
  deletePMWorkflow,
  parkPMTask,
  schedulePMTask,
  queuePMTaskToday,
  updatePMChecklistComment,
} from '../utils/api';

const PracticeManagementContext = createContext(null);

export function PracticeManagementProvider({ children, unitId = 'a0000000-0000-4000-8000-000000000101', readOnly = false }) {
  const { getToken, user } = useAuth();
  const userName = user?.attributes?.name || 'Unknown';
  const { toast } = useToast();

  const onMutationError = useCallback((action) => {
    toast({ variant: 'destructive', title: 'Save failed', description: `Could not ${action}. Your change may not be saved.` });
  }, [toast]);

  const [taskLibrary, setTaskLibrary] = useState([]);
  const [priorityQueue, setPriorityQueue] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [overviewMetrics, setOverviewMetrics] = useState(null);
  const [staffRoster, setStaffRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChecklist, setActiveChecklist] = useState(null);

  // Load all data on mount
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const today = new Date().toISOString().slice(0, 10);

    Promise.all([
      getPMTaskLibrary(unitId, getToken),
      getPMPriorityQueue(unitId, today, getToken),
      getPMChecklists(unitId, getToken),
      getPMOverviewMetrics(unitId, getToken),
      getPMStaffRoster(unitId, getToken),
      getPMWorkflows(getToken),
    ]).then(([tasks, queue, cls, metrics, staff, wfs]) => {
      if (cancelled) return;
      setTaskLibrary(tasks);
      setPriorityQueue(queue);
      setChecklists(cls);
      setOverviewMetrics(metrics);
      setStaffRoster(staff);
      setWorkflows(wfs || []);
      setLoading(false);
    }).catch((err) => {
      if (cancelled) return;
      console.warn('[PracticeManagementContext] Initial load failed:', err?.message);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [unitId, getToken]);

  // ── Task Library Actions ──

  const toggleTaskActive = useCallback((taskId) => {
    if (readOnly) return;
    setTaskLibrary((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, active: !t.active } : t))
    );
    // Persist to backend
    const task = taskLibrary.find((t) => t.id === taskId);
    if (task) updatePMTask(taskId, { active: !task.active }, getToken).catch(() => onMutationError('toggle task'));
  }, [readOnly, taskLibrary, getToken, onMutationError]);

  const updateTask = useCallback((taskId, updates) => {
    if (readOnly) return;
    setTaskLibrary((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, ...updates } : t))
    );
    updatePMTask(taskId, updates, getToken).catch(() => onMutationError('update task'));
  }, [readOnly, getToken, onMutationError]);

  const addCustomTask = useCallback((task) => {
    if (readOnly) return;
    const newTask = {
      ...task,
      id: `task-custom-${Date.now()}`,
      isCustom: true,
      active: true,
      originalVersion: null,
    };
    setTaskLibrary((prev) => [...prev, newTask]);
    createPMTask(task, getToken).catch(() => onMutationError('create task'));
  }, [readOnly, getToken, onMutationError]);

  const restoreTask = useCallback((taskId) => {
    if (readOnly) return;
    setTaskLibrary((prev) =>
      prev.map((t) => {
        if (t.id !== taskId || !t.originalVersion) return t;
        return { ...t.originalVersion, id: t.id };
      })
    );
    restorePMTask(taskId, getToken).catch(() => onMutationError('restore task'));
  }, [readOnly, getToken, onMutationError]);

  // ── Priority Queue Actions ──

  const completeTask = useCallback((instanceId, { note, linkedOutputs } = {}) => {
    if (readOnly) return;
    setPriorityQueue((prev) =>
      prev.map((inst) =>
        inst.id === instanceId
          ? {
              ...inst,
              status: 'completed',
              completedAt: new Date().toISOString(),
              completedBy: userName,
              completionNote: note || null,
              linkedOutputs: linkedOutputs || [],
            }
          : inst
      )
    );
    completePMTask(instanceId, { note, linkedOutputs }, getToken).catch(() => onMutationError('complete task'));
  }, [readOnly, getToken, userName, onMutationError]);

  const skipTask = useCallback((instanceId, reason) => {
    if (readOnly) return;
    setPriorityQueue((prev) =>
      prev.map((inst) =>
        inst.id === instanceId
          ? { ...inst, status: 'skipped', skipReason: reason }
          : inst
      )
    );
    skipPMTask(instanceId, reason, getToken).catch(() => onMutationError('skip task'));
  }, [readOnly, getToken, onMutationError]);

  const delegateTask = useCallback((instanceId, delegatee) => {
    if (readOnly) return;
    setPriorityQueue((prev) =>
      prev.map((inst) => {
        if (inst.id !== instanceId) return inst;
        return {
          ...inst,
          status: 'delegated',
          delegatedTo: delegatee,
          delegationHistory: [
            ...inst.delegationHistory,
            { delegatee, assignedAt: new Date().toISOString(), status: 'pending' },
          ],
        };
      })
    );
    delegatePMTask(instanceId, delegatee, getToken).catch(() => onMutationError('delegate task'));
  }, [readOnly, getToken, onMutationError]);

  const reassignOrphanedTask = useCallback((instanceId, newOwner) => {
    if (readOnly) return;
    setPriorityQueue((prev) =>
      prev.map((inst) =>
        inst.id === instanceId
          ? { ...inst, isOrphaned: false, resolvedOwner: newOwner, status: 'pending' }
          : inst
      )
    );
    reassignPMOrphanedTask(instanceId, newOwner, getToken).catch(() => onMutationError('reassign task'));
  }, [readOnly, getToken, onMutationError]);

  const deferOrphanedTask = useCallback((instanceId, deferDate, reason) => {
    if (readOnly) return;
    setPriorityQueue((prev) =>
      prev.map((inst) =>
        inst.id === instanceId
          ? { ...inst, isOrphaned: false, status: 'skipped', skipReason: `Deferred to ${deferDate}: ${reason}`, dueDate: deferDate }
          : inst
      )
    );
    deferPMOrphanedTask(instanceId, deferDate, reason, getToken).catch(() => onMutationError('defer task'));
  }, [readOnly, getToken, onMutationError]);

  const reorderQueue = useCallback((fromIndex, toIndex) => {
    if (readOnly) return;
    let movedId = null;
    setPriorityQueue((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      movedId = moved?.id;
      next.splice(toIndex, 0, moved);
      return next;
    });
    if (movedId) reorderPMQueue(movedId, toIndex, getToken).catch(() => onMutationError('reorder queue'));
  }, [readOnly, getToken, onMutationError]);

  // ── Checklist Actions ──

  const toggleChecklistStep = useCallback((checklistId, stepId) => {
    if (readOnly) return;
    // Determine the new completed state before updating
    const cl = checklists.find((c) => c.id === checklistId);
    const step = cl?.steps.find((s) => s.id === stepId);
    const newCompleted = step ? !step.completed : true;

    setChecklists((prev) =>
      prev.map((c) => {
        if (c.id !== checklistId) return c;
        const steps = c.steps.map((s) => {
          if (s.id !== stepId) return s;
          return {
            ...s,
            completed: newCompleted,
            completedAt: newCompleted ? new Date().toISOString() : null,
            completedBy: newCompleted ? userName : null,
          };
        });
        return { ...c, steps, stepsCompleted: steps.filter((s) => s.completed).length };
      })
    );
    updatePMChecklistStep(checklistId, stepId, { completed: newCompleted }, getToken).catch(() => onMutationError('update checklist'));
  }, [readOnly, checklists, getToken, userName, onMutationError]);

  const updateStepNote = useCallback((checklistId, stepId, note) => {
    if (readOnly) return;
    setChecklists((prev) =>
      prev.map((cl) => {
        if (cl.id !== checklistId) return cl;
        return {
          ...cl,
          steps: cl.steps.map((s) => (s.id === stepId ? { ...s, note } : s)),
        };
      })
    );
    updatePMChecklistStep(checklistId, stepId, { note }, getToken).catch(() => onMutationError('update note'));
  }, [readOnly, getToken, onMutationError]);

  // ── Workflow Template Actions ──

  const createWorkflow = useCallback(async (workflowData) => {
    if (readOnly) return null;
    // Optimistic insert with temp id
    const tempId = `wf-temp-${Date.now()}`;
    const optimistic = { id: tempId, ...workflowData };
    setWorkflows((prev) => [...prev, optimistic]);
    try {
      const created = await createPMWorkflow(workflowData, getToken);
      // Swap temp id for server id
      setWorkflows((prev) => prev.map((w) => (w.id === tempId ? created : w)));
      return created;
    } catch (err) {
      // Rollback
      setWorkflows((prev) => prev.filter((w) => w.id !== tempId));
      onMutationError('create workflow');
      throw err;
    }
  }, [readOnly, getToken, onMutationError]);

  const updateWorkflow = useCallback(async (workflowId, updates) => {
    if (readOnly) return null;
    let snapshot = null;
    setWorkflows((prev) => {
      snapshot = prev.find((w) => w.id === workflowId);
      return prev.map((w) => (w.id === workflowId ? { ...w, ...updates } : w));
    });
    try {
      const updated = await updatePMWorkflow(workflowId, updates, getToken);
      setWorkflows((prev) => prev.map((w) => (w.id === workflowId ? updated : w)));
      return updated;
    } catch (err) {
      // Rollback
      if (snapshot) {
        setWorkflows((prev) => prev.map((w) => (w.id === workflowId ? snapshot : w)));
      }
      onMutationError('update workflow');
      throw err;
    }
  }, [readOnly, getToken, onMutationError]);

  const deleteWorkflow = useCallback(async (workflowId) => {
    if (readOnly) return false;
    let snapshot = null;
    setWorkflows((prev) => {
      snapshot = prev.find((w) => w.id === workflowId);
      return prev.filter((w) => w.id !== workflowId);
    });
    try {
      await deletePMWorkflow(workflowId, getToken);
      return true;
    } catch (err) {
      // Rollback
      if (snapshot) setWorkflows((prev) => [...prev, snapshot]);
      onMutationError('delete workflow');
      throw err;
    }
  }, [readOnly, getToken, onMutationError]);

  // ── Kanban Bucket Actions ──

  const parkTask = useCallback((instanceId) => {
    if (readOnly) return;
    setPriorityQueue((prev) =>
      prev.map((inst) => inst.id === instanceId ? { ...inst, bucket: 'parked' } : inst)
    );
    parkPMTask(instanceId, getToken).catch(() => onMutationError('park task'));
  }, [readOnly, getToken, onMutationError]);

  const scheduleTask = useCallback((instanceId, dueDate) => {
    if (readOnly) return;
    setPriorityQueue((prev) =>
      prev.map((inst) => inst.id === instanceId ? { ...inst, bucket: 'scheduled', dueDate } : inst)
    );
    schedulePMTask(instanceId, dueDate, getToken).catch(() => onMutationError('schedule task'));
  }, [readOnly, getToken, onMutationError]);

  const queueTask = useCallback((instanceId) => {
    if (readOnly) return;
    const today = new Date().toISOString().slice(0, 10);
    setPriorityQueue((prev) =>
      prev.map((inst) => inst.id === instanceId ? { ...inst, bucket: 'today', dueDate: today } : inst)
    );
    queuePMTaskToday(instanceId, getToken).catch(() => onMutationError('move task to today'));
  }, [readOnly, getToken, onMutationError]);

  const updateWorkflowComment = useCallback((checklistId, comment) => {
    if (readOnly) return;
    setChecklists((prev) =>
      prev.map((cl) => cl.id === checklistId ? { ...cl, comments: comment } : cl)
    );
    updatePMChecklistComment(checklistId, comment, getToken).catch(() => onMutationError('update comment'));
  }, [readOnly, getToken, onMutationError]);

  // ── Workflow-Available Nodes (unified task stream) ──

  const workflowAvailableNodes = useMemo(() => {
    // Extract "available" task nodes from active checklists/workflows
    // and shape them as queue-compatible items
    const nodes = [];
    for (const cl of checklists) {
      if (!cl.steps) continue;
      // Find first incomplete step (simplified linear DAG evaluation)
      for (const step of cl.steps) {
        if (step.completed) continue;
        nodes.push({
          id: `wf-${cl.id}-${step.id}`,
          taskId: step.id,
          taskName: step.description,
          domain: cl.domain,
          dueDate: new Date().toLocaleDateString('en-CA').slice(0, 10),
          dueTime: '08:00',
          status: 'pending',
          ownerRole: 'com',
          resolvedOwner: { name: userName, id: user?.attributes?.sub || 'unknown' },
          delegatedTo: null,
          delegationHistory: [],
          completedAt: null,
          completedBy: null,
          completionNote: null,
          completionType: step.requiresNote ? 'checkbox_note' : 'checkbox',
          linkedOutputs: [],
          skipReason: null,
          isOrphaned: false,
          overdueHours: 0,
          estimatedMinutes: 5,
          criticalityTier: 'B',
          // Workflow metadata
          source: 'workflow',
          workflowId: cl.id,
          workflowName: cl.name,
        });
        break; // Only surface the first available step per workflow
      }
    }
    return nodes;
  }, [checklists, userName, user]);

  // ── Derived State ──

  const orphanedTasks = useMemo(
    () => priorityQueue.filter((t) => t.isOrphaned),
    [priorityQueue]
  );

  const overdueTasks = useMemo(
    () => priorityQueue.filter((t) => t.status === 'overdue' || t.overdueHours > 0),
    [priorityQueue]
  );

  const value = useMemo(() => ({
    // State
    unitId,
    taskLibrary,
    priorityQueue,
    checklists,
    workflows,
    overviewMetrics,
    staffRoster,
    loading,
    readOnly,
    activeChecklist,
    orphanedTasks,
    overdueTasks,
    workflowAvailableNodes,
    // Task Library Actions
    toggleTaskActive,
    updateTask,
    addCustomTask,
    restoreTask,
    // Priority Queue Actions
    completeTask,
    skipTask,
    delegateTask,
    reassignOrphanedTask,
    deferOrphanedTask,
    reorderQueue,
    // Kanban Bucket Actions
    parkTask,
    scheduleTask,
    queueTask,
    updateWorkflowComment,
    // Checklist Actions
    setActiveChecklist,
    toggleChecklistStep,
    updateStepNote,
    // Workflow Template Actions
    createWorkflow,
    updateWorkflow,
    deleteWorkflow,
  }), [
    unitId, taskLibrary, priorityQueue, checklists, workflows, overviewMetrics, staffRoster,
    loading, readOnly, activeChecklist, orphanedTasks, overdueTasks, workflowAvailableNodes,
    toggleTaskActive, updateTask, addCustomTask, restoreTask,
    completeTask, skipTask, delegateTask, reassignOrphanedTask,
    deferOrphanedTask, reorderQueue, parkTask, scheduleTask, queueTask, updateWorkflowComment,
    toggleChecklistStep, updateStepNote, createWorkflow, updateWorkflow, deleteWorkflow,
  ]);

  return (
    <PracticeManagementContext.Provider value={value}>
      {children}
    </PracticeManagementContext.Provider>
  );
}

export function usePracticeManagement() {
  const ctx = useContext(PracticeManagementContext);
  if (!ctx) throw new Error('usePracticeManagement must be used within PracticeManagementProvider');
  return ctx;
}

export default PracticeManagementContext;
